import { FLOAT_REGEX } from "./regexValidations";

/**
 * A location with its own consumer payout rate and its own Droppie revenue
 * per kg for a material. An exception always sets both.
 */
export interface ILocationRate {
  /** Location id; the backend may also return a populated location. */
  location: string | { _id: string; name?: string };
  rate: number;
  /** Missing only on exceptions saved before revenue was part of them. */
  droppie_revenue_kg?: number;
}

const locationIdOf = (location: ILocationRate["location"]): string =>
  typeof location === "object" ? String(location?._id) : String(location);

/**
 * The consumer payout rate (euros per kg/item) of a material at a location:
 * the location's exception when there is one, otherwise the global rate.
 * Same rule as the backend's resolveRate — the backend decides the payout,
 * this is for showing prices up front.
 */
export const effectiveRate = (
  material:
    | { rate?: number | string; locationRates?: ILocationRate[] }
    | null
    | undefined,
  locationId?: string | null
): number => {
  if (locationId) {
    const exception = material?.locationRates?.find(
      (lr) => locationIdOf(lr.location) === String(locationId)
    );
    if (exception) return Number(exception.rate ?? 0);
  }
  return Number(material?.rate ?? 0);
};

/** A drop line as stored: what it weighed, what it was paid, its snapshot. */
interface IPaidDropLine {
  weight?: number | string;
  price?: number;
  priceSnapshot?: { rate?: number | null } | null;
}

/**
 * The rate a drop line was paid at: its snapshot's rate when that gives the
 * stored price, else what it paid per kg/item; null when that cannot be told.
 * Same rule as the backend's paidRateOf (drops.service).
 */
export const paidRateOf = (
  line: IPaidDropLine | null | undefined
): number | null => {
  const weight = Number(line?.weight ?? 0);
  const price = Number(line?.price ?? 0);
  const snapshotRate = line?.priceSnapshot?.rate;
  if (
    snapshotRate !== undefined &&
    snapshotRate !== null &&
    Math.round(weight * Number(snapshotRate) * 100) === Math.round(price)
  ) {
    return Number(snapshotRate);
  }
  return weight > 0 ? price / (weight * 100) : null;
};

/**
 * The price (cents) the backend stores when a drop line's weight is edited:
 * the same weight keeps its price, another weight is priced at the rate the
 * line was paid at, and only a line without a known paid rate gets the
 * material's current rate at the drop's location. Same rule as the backend's
 * priceDropLines; the backend decides, this is the preview.
 */
export const editedLinePrice = (
  original: IPaidDropLine | null | undefined,
  weight: number,
  material: Parameters<typeof effectiveRate>[0],
  locationId?: string | null
): number => {
  if (original && Number(original.weight) === weight) {
    return Number(original.price ?? 0);
  }
  const rate = paidRateOf(original) ?? effectiveRate(material, locationId);
  return Math.round(weight * rate * 100);
};

/** One row in the location exception editor; numbers are kept as typed text. */
export interface ILocationRateRow {
  location: string;
  rate: string;
  revenue: string;
}

export const toLocationRateRows = (
  locationRates?: ILocationRate[]
): ILocationRateRow[] =>
  (locationRates ?? []).map((lr) => ({
    location: locationIdOf(lr.location),
    rate: String(lr.rate ?? ""),
    revenue: String(lr.droppie_revenue_kg ?? ""),
  }));

export const toLocationRates = (
  rows: ILocationRateRow[]
): { location: string; rate: number; droppie_revenue_kg: number }[] =>
  rows.map((row) => ({
    location: row.location,
    rate: Number(row.rate),
    droppie_revenue_kg: Number(row.revenue),
  }));

/** A validation error for one field of an editor row. */
export interface ILocationRateRowError {
  field: "location" | "rate" | "revenue";
  message: string;
}

const isNonNegativeNumber = (value: string) =>
  value.trim() !== "" &&
  FLOAT_REGEX.test(value.trim()) &&
  // FLOAT_REGEX also matches a lone "."
  Number.isFinite(Number(value.trim()));

/**
 * Per-row error (or null), mirroring the backend's checks: a location picked,
 * at most once, and both a rate and a revenue of 0 or more.
 */
export const validateLocationRateRows = (
  rows: ILocationRateRow[]
): (ILocationRateRowError | null)[] =>
  rows.map((row, index) => {
    if (!row.location) return { field: "location", message: "Pick a location" };
    if (rows.findIndex((r) => r.location === row.location) !== index) {
      return { field: "location", message: "This location already has an exception" };
    }
    if (!isNonNegativeNumber(row.rate)) {
      return { field: "rate", message: "Enter a rate of 0 or more" };
    }
    if (!isNonNegativeNumber(row.revenue)) {
      return { field: "revenue", message: "Enter a revenue of 0 or more" };
    }
    return null;
  });
